(function (global) {
  'use strict';

  var ELEMENT_NAME = 'about-events-gallery';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function toArray(collection) {
    return Array.prototype.slice.call(collection || []);
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function prefersReducedMotion() {
    return typeof global.matchMedia === 'function'
      && global.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  function replaceChildren(target, content) {
    if (!target) return;

    if (typeof target.replaceChildren === 'function') {
      if (content) target.replaceChildren(content);
      else target.replaceChildren();
      return;
    }

    while (target.firstChild) target.removeChild(target.firstChild);
    if (content) target.appendChild(content);
  }

  function getClosestCardIndex(track, cards) {
    if (!track || !cards.length) return 0;

    var firstOffset = Number(cards[0].offsetLeft) || 0;
    var scrollLeft = Number(track.scrollLeft) || 0;
    var closestIndex = 0;
    var closestDistance = Infinity;

    cards.forEach(function (card, index) {
      var target = (Number(card.offsetLeft) || 0) - firstOffset;
      var distance = Math.abs(target - scrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  function buildMeta(location, date) {
    return [location, date].filter(Boolean).join(' · ');
  }

  function hasRealOverflow(track, tolerance) {
    if (!track) return false;

    var allowedDifference = Number.isFinite(tolerance) ? tolerance : 2;
    return Number(track.scrollWidth) > Number(track.clientWidth) + allowedDifference;
  }

  if (!global.HTMLElement || !global.customElements) return;

  class CmxAboutEventsGalleryElement extends global.HTMLElement {
    constructor() {
      super();

      this.initialized = false;
      this.expanded = false;
      this.activeEventIndex = 0;
      this.currentPage = 0;
      this.visibleCards = 1;
      this.totalPages = 1;
      this.hasOverflow = false;
      this.activePhotoIndex = 0;
      this.activeTrigger = null;
      this.galleryItems = [];
      this.preloadedImages = [];
      this.scrollFrame = null;
      this.pointerStart = null;
      this.scrollLockState = null;
      this.resizeObserver = null;
      this.mutationObserver = null;
      this.layoutFrame = null;

      this.handleRootClick = this.handleRootClick.bind(this);
      this.handleTrackScroll = this.handleTrackScroll.bind(this);
      this.handleTrackKeydown = this.handleTrackKeydown.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogCancel = this.handleDialogCancel.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.handleDialogKeydown = this.handleDialogKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handlePointerCancel = this.handlePointerCancel.bind(this);
      this.handleMediaLoad = this.handleMediaLoad.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.scheduleEventControlsUpdate = this.scheduleEventControlsUpdate.bind(this);
      this.updateEventControls = this.updateEventControls.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-cmx-about-events-track]');
      this.cards = this.track
        ? toArray(this.track.querySelectorAll('[data-cmx-about-event-card]'))
        : [];
      this.previousEventButton = this.querySelector('[data-cmx-about-events-previous]');
      this.nextEventButton = this.querySelector('[data-cmx-about-events-next]');
      this.toolbar = this.querySelector('[data-cmx-about-events-toolbar]');
      this.navigation = this.querySelector('[data-cmx-about-events-navigation]');
      this.progressTrack = this.querySelector('.cmx-about-events__progress');
      this.progress = this.querySelector('[data-cmx-about-events-progress]');
      this.eventCounter = this.querySelector('[data-cmx-about-events-counter]');
      this.viewToggle = this.querySelector('[data-cmx-about-events-view-toggle]');
      this.dialog = this.querySelector('[data-cmx-about-events-dialog]');
      this.dialogClose = this.querySelector('[data-cmx-about-events-dialog-close]');
      this.mainImage = this.querySelector('[data-cmx-about-events-main-image]');
      this.photoCounter = this.querySelector('[data-cmx-about-events-photo-counter]');
      this.caption = this.querySelector('[data-cmx-about-events-caption]');
      this.thumbnails = this.querySelector('[data-cmx-about-events-thumbnails]');
      this.photoPreviousButton = this.querySelector('[data-cmx-about-events-photo-previous]');
      this.photoNextButton = this.querySelector('[data-cmx-about-events-photo-next]');
      this.swipeArea = this.querySelector('[data-cmx-about-events-swipe-area]');
      this.modalType = this.querySelector('[data-cmx-about-events-modal-type]');
      this.modalTitle = this.querySelector('[data-cmx-about-events-modal-title]');
      this.modalMeta = this.querySelector('[data-cmx-about-events-modal-meta]');
      this.modalDescription = this.querySelector('[data-cmx-about-events-modal-description]');
      this.modalFooter = this.querySelector('[data-cmx-about-events-modal-footer]');

      this.initialized = true;
      this.addEventListener('click', this.handleRootClick);
      this.addEventListener('load', this.handleMediaLoad, true);

      if (typeof global.addEventListener === 'function') {
        global.addEventListener('resize', this.scheduleEventControlsUpdate, { passive: true });
        global.addEventListener('orientationchange', this.scheduleEventControlsUpdate, { passive: true });
      }

      if (global.document && typeof global.document.addEventListener === 'function') {
        global.document.addEventListener('shopify:section:load', this.handleSectionLoad);
      }

      if (this.track) {
        this.track.addEventListener('scroll', this.handleTrackScroll, { passive: true });
        this.track.addEventListener('keydown', this.handleTrackKeydown);
      }

      if (this.dialog) {
        this.dialog.addEventListener('click', this.handleDialogClick);
        this.dialog.addEventListener('cancel', this.handleDialogCancel);
        this.dialog.addEventListener('close', this.handleDialogClose);
        this.dialog.addEventListener('keydown', this.handleDialogKeydown);
      }

      if (this.swipeArea) {
        this.swipeArea.addEventListener('pointerdown', this.handlePointerDown);
        this.swipeArea.addEventListener('pointerup', this.handlePointerUp);
        this.swipeArea.addEventListener('pointercancel', this.handlePointerCancel);
      }

      if (typeof global.ResizeObserver === 'function') {
        this.resizeObserver = new global.ResizeObserver(this.scheduleEventControlsUpdate);
        this.resizeObserver.observe(this);
        if (this.track) this.resizeObserver.observe(this.track);
      }

      if (this.track && typeof global.MutationObserver === 'function') {
        this.mutationObserver = new global.MutationObserver(this.scheduleEventControlsUpdate);
        this.mutationObserver.observe(this.track, { childList: true });
      }

      this.updateEventControls();
      this.scheduleEventControlsUpdate();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.removeEventListener('click', this.handleRootClick);
      this.removeEventListener('load', this.handleMediaLoad, true);

      if (typeof global.removeEventListener === 'function') {
        global.removeEventListener('resize', this.scheduleEventControlsUpdate);
        global.removeEventListener('orientationchange', this.scheduleEventControlsUpdate);
      }

      if (global.document && typeof global.document.removeEventListener === 'function') {
        global.document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      }

      if (this.track) {
        this.track.removeEventListener('scroll', this.handleTrackScroll);
        this.track.removeEventListener('keydown', this.handleTrackKeydown);
      }

      if (this.dialog) {
        this.dialog.removeEventListener('click', this.handleDialogClick);
        this.dialog.removeEventListener('cancel', this.handleDialogCancel);
        this.dialog.removeEventListener('close', this.handleDialogClose);
        this.dialog.removeEventListener('keydown', this.handleDialogKeydown);
      }

      if (this.swipeArea) {
        this.swipeArea.removeEventListener('pointerdown', this.handlePointerDown);
        this.swipeArea.removeEventListener('pointerup', this.handlePointerUp);
        this.swipeArea.removeEventListener('pointercancel', this.handlePointerCancel);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }

      if (this.scrollFrame !== null && typeof global.cancelAnimationFrame === 'function') {
        global.cancelAnimationFrame(this.scrollFrame);
      }

      if (this.layoutFrame !== null && typeof global.cancelAnimationFrame === 'function') {
        global.cancelAnimationFrame(this.layoutFrame);
      }

      this.activeTrigger = null;
      if (this.dialog && this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      }
      this.unlockScroll();
      this.initialized = false;
    }

    handleRootClick(event) {
      var target = event.target;
      if (!(target instanceof global.Element)) return;

      var previous = target.closest('[data-cmx-about-events-previous]');
      if (previous) {
        this.scrollToPage(this.currentPage - 1);
        return;
      }

      var next = target.closest('[data-cmx-about-events-next]');
      if (next) {
        this.scrollToPage(this.currentPage + 1);
        return;
      }

      var viewToggle = target.closest('[data-cmx-about-events-view-toggle]');
      if (viewToggle) {
        this.toggleView();
        return;
      }

      var galleryTrigger = target.closest('[data-cmx-about-event-open]');
      if (galleryTrigger) {
        this.openGallery(galleryTrigger);
        return;
      }

      var closeButton = target.closest('[data-cmx-about-events-dialog-close]');
      if (closeButton) {
        this.closeGallery();
        return;
      }

      var previousPhoto = target.closest('[data-cmx-about-events-photo-previous]');
      if (previousPhoto) {
        this.showPhoto(this.activePhotoIndex - 1);
        return;
      }

      var nextPhoto = target.closest('[data-cmx-about-events-photo-next]');
      if (nextPhoto) {
        this.showPhoto(this.activePhotoIndex + 1);
        return;
      }

      var thumbnail = target.closest('[data-cmx-about-events-thumbnail]');
      if (thumbnail) {
        this.showPhoto(parseInt(thumbnail.dataset.photoIndex, 10) || 0);
      }
    }

    handleTrackScroll() {
      if (this.expanded || this.scrollFrame !== null) return;

      if (typeof global.requestAnimationFrame !== 'function') {
        this.updateEventControls();
        return;
      }

      this.scrollFrame = global.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateEventControls();
      });
    }

    handleTrackKeydown(event) {
      if (event.target !== this.track || this.expanded) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollToPage(this.currentPage - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollToPage(this.currentPage + 1);
      }
    }

    setControlVisibility(control, visible) {
      if (!control) return;

      if (!visible
        && global.document
        && global.document.activeElement
        && typeof control.contains === 'function'
        && control.contains(global.document.activeElement)
        && this.track
        && typeof this.track.focus === 'function') {
        this.track.focus({ preventScroll: true });
      }

      control.hidden = !visible;
      control.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if ('disabled' in control && !visible) control.disabled = true;
    }

    handleMediaLoad(event) {
      if (!this.track || !event.target || event.target.tagName !== 'IMG') return;
      if (typeof this.track.contains === 'function' && !this.track.contains(event.target)) return;

      this.scheduleEventControlsUpdate();
    }

    handleSectionLoad(event) {
      if (!event || !event.target) return;

      var belongsToSection = event.target === this
        || (typeof event.target.contains === 'function' && event.target.contains(this))
        || (typeof this.contains === 'function' && this.contains(event.target));
      if (belongsToSection) this.scheduleEventControlsUpdate();
    }

    scheduleEventControlsUpdate() {
      if (!this.initialized || this.layoutFrame !== null) return;

      if (typeof global.requestAnimationFrame !== 'function') {
        this.cards = this.track
          ? toArray(this.track.querySelectorAll('[data-cmx-about-event-card]'))
          : [];
        this.updateEventControls();
        return;
      }

      this.layoutFrame = global.requestAnimationFrame(() => {
        this.layoutFrame = null;
        this.cards = this.track
          ? toArray(this.track.querySelectorAll('[data-cmx-about-event-card]'))
          : [];
        this.updateEventControls();
      });
    }

    getVisibleCardCount() {
      if (!this.track || !this.cards.length) return 1;

      var tolerance = 2;
      var trackRect = typeof this.track.getBoundingClientRect === 'function'
        ? this.track.getBoundingClientRect()
        : null;
      var visible = 0;

      if (trackRect && trackRect.width > 0) {
        this.cards.forEach(function (card) {
          if (typeof card.getBoundingClientRect !== 'function') return;

          var cardRect = card.getBoundingClientRect();
          if (cardRect.width > 0
            && cardRect.left >= trackRect.left - tolerance
            && cardRect.right <= trackRect.right + tolerance) {
            visible += 1;
          }
        });
      }

      if (visible > 0) return visible;

      var cardWidth = Number(this.cards[0].getBoundingClientRect
        ? this.cards[0].getBoundingClientRect().width
        : 0);
      var trackWidth = Number(this.track.clientWidth) || 0;
      if (cardWidth > 0 && trackWidth > 0) {
        return Math.max(1, Math.floor(trackWidth / cardWidth));
      }

      return 1;
    }

    getPageOffset(page) {
      if (!this.cards.length) return 0;

      var firstOffset = Number(this.cards[0].offsetLeft) || 0;
      var firstIndex = Math.min(page * this.visibleCards, this.cards.length - this.visibleCards);
      return Math.max(0, (Number(this.cards[firstIndex].offsetLeft) || 0) - firstOffset);
    }

    getClosestPage() {
      if (!this.track || !this.cards.length) return 0;

      var scrollLeft = Number(this.track.scrollLeft) || 0;
      var closestPage = 0;
      var closestDistance = Infinity;

      for (var page = 0; page < this.totalPages; page += 1) {
        var distance = Math.abs(this.getPageOffset(page) - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = page;
        }
      }

      return closestPage;
    }

    updateEventControls() {
      if (!this.cards.length) {
        this.hasOverflow = false;
        this.visibleCards = 1;
        this.totalPages = 1;
        this.setControlVisibility(this.previousEventButton, false);
        this.setControlVisibility(this.nextEventButton, false);
        this.setControlVisibility(this.progressTrack, false);
        this.setControlVisibility(this.eventCounter, false);
        this.setControlVisibility(this.navigation, false);
        this.setControlVisibility(this.viewToggle, false);
        this.setControlVisibility(this.toolbar, false);
        if (this.track) this.track.tabIndex = -1;
        return;
      }

      var total = this.cards.length;
      var overflow = hasRealOverflow(this.track, 2);

      this.visibleCards = this.getVisibleCardCount();
      this.totalPages = Math.max(1, Math.ceil(total / this.visibleCards));
      this.hasOverflow = overflow && this.totalPages > 1;

      if (!this.hasOverflow && !this.expanded) {
        this.currentPage = 0;
        this.activeEventIndex = 0;
        this.classList.add('cmx-about-events__component--static');
        this.cards.forEach(function (card) { card.removeAttribute('aria-current'); });
        if (this.track && this.track.scrollLeft) this.track.scrollLeft = 0;
      } else {
        this.classList.remove('cmx-about-events__component--static');
        if (!this.expanded) this.currentPage = this.getClosestPage();
        this.currentPage = clamp(this.currentPage, 0, this.totalPages - 1);
        this.activeEventIndex = Math.min(this.currentPage * this.visibleCards, total - 1);
        this.cards.forEach(function (card) { card.removeAttribute('aria-current'); });
      }

      var navigationVisible = this.hasOverflow && !this.expanded;
      var paginationVisible = navigationVisible && this.totalPages > 1;
      this.setControlVisibility(this.previousEventButton, navigationVisible);
      this.setControlVisibility(this.nextEventButton, navigationVisible);
      this.setControlVisibility(this.progressTrack, paginationVisible);
      this.setControlVisibility(this.eventCounter, paginationVisible);
      this.setControlVisibility(this.navigation, navigationVisible);
      this.setControlVisibility(this.viewToggle, Boolean(this.viewToggle) && (this.hasOverflow || this.expanded));
      this.setControlVisibility(this.toolbar, navigationVisible || Boolean(this.expanded && this.viewToggle));

      if (this.track) this.track.tabIndex = navigationVisible ? 0 : -1;

      if (navigationVisible) {
        if (this.previousEventButton) this.previousEventButton.disabled = this.currentPage <= 0;
        if (this.nextEventButton) this.nextEventButton.disabled = this.currentPage >= this.totalPages - 1;
        if (this.eventCounter) {
          var counterText = String(this.currentPage + 1) + ' / ' + String(this.totalPages);
          if (this.eventCounter.textContent.trim() !== counterText) {
            this.eventCounter.textContent = counterText;
          }
          this.eventCounter.setAttribute('aria-label', 'Página ' + String(this.currentPage + 1) + ' de ' + String(this.totalPages));
        }
        if (this.progress) {
          this.progress.style.width = String(((this.currentPage + 1) / this.totalPages) * 100) + '%';
        }
        if (this.progressTrack) {
          this.progressTrack.setAttribute('aria-valuenow', String(this.currentPage + 1));
          this.progressTrack.setAttribute('aria-valuemax', String(this.totalPages));
        }
      }
    }

    scrollToEvent(index, behavior) {
      if (!this.track || !this.cards.length || this.expanded) return;

      var position = clamp(index, 0, this.cards.length - 1);
      var firstOffset = Number(this.cards[0].offsetLeft) || 0;
      var targetOffset = (Number(this.cards[position].offsetLeft) || 0) - firstOffset;

      this.activeEventIndex = position;
      this.track.scrollTo({
        left: targetOffset,
        behavior: behavior || (prefersReducedMotion() ? 'auto' : 'smooth')
      });
      this.updateEventControls();
    }

    scrollToPage(page, behavior) {
      if (!this.track || !this.cards.length || this.expanded || !this.hasOverflow) return;

      this.currentPage = clamp(page, 0, this.totalPages - 1);
      this.activeEventIndex = Math.min(this.currentPage * this.visibleCards, this.cards.length - 1);
      this.track.scrollTo({
        left: this.getPageOffset(this.currentPage),
        behavior: behavior || (prefersReducedMotion() ? 'auto' : 'smooth')
      });
      this.updateEventControls();
    }

    toggleView() {
      if (!this.viewToggle || !this.cards.length) return;

      this.expanded = !this.expanded;
      this.classList.toggle('cmx-about-events__component--grid', this.expanded);
      this.viewToggle.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
      this.viewToggle.textContent = this.expanded ? 'Volver al carrusel' : 'Ver todos los eventos';

      if (!this.expanded) {
        var page = this.currentPage;
        global.requestAnimationFrame(function () {
          this.updateEventControls();
          this.scrollToPage(page, 'auto');
        }.bind(this));
      } else {
        this.updateEventControls();
      }
    }

    openGallery(trigger) {
      if (!this.dialog) return;

      var card = trigger.closest('[data-cmx-about-event-card]');
      var galleryTemplate = card && card.querySelector('[data-cmx-about-event-gallery]');
      if (!card || !galleryTemplate) return;

      var items = toArray(galleryTemplate.content.querySelectorAll('[data-cmx-about-event-image]'))
        .map(function (item) {
          return {
            src: item.dataset.src || '',
            srcset: item.dataset.srcset || '',
            thumbnailSrc: item.dataset.thumbnailSrc || '',
            thumbnailSrcset: item.dataset.thumbnailSrcset || '',
            width: parseInt(item.dataset.width, 10) || 1600,
            height: parseInt(item.dataset.height, 10) || 1200,
            alt: item.dataset.alt || '',
            caption: item.dataset.caption || ''
          };
        })
        .filter(function (item) { return Boolean(item.src); });

      if (!items.length) return;

      this.activeEventIndex = parseInt(card.dataset.eventIndex, 10) || 0;
      this.updateEventControls();
      this.activeTrigger = trigger;
      this.galleryItems = items;
      this.activePhotoIndex = 0;
      this.populateEventInformation(card);
      this.buildThumbnails();
      this.showPhoto(0);
      this.lockScroll();

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
        this.dialog.setAttribute('role', 'dialog');
        this.dialog.setAttribute('aria-modal', 'true');
      }

      global.requestAnimationFrame(() => {
        if (this.dialogClose) this.dialogClose.focus();
      });
    }

    populateEventInformation(card) {
      var title = card.dataset.eventTitle || '';
      var type = card.dataset.eventType || '';
      var meta = buildMeta(card.dataset.eventLocation || '', card.dataset.eventDate || '');
      var footer = card.dataset.eventGalleryFooter || '';
      var descriptionTemplate = card.querySelector('[data-cmx-about-event-description]');

      if (this.modalTitle) this.modalTitle.textContent = title;
      if (this.modalType) this.modalType.textContent = type;

      if (this.modalMeta) {
        this.modalMeta.textContent = meta;
        this.modalMeta.hidden = !meta;
      }

      if (this.modalDescription) {
        var description = descriptionTemplate
          ? descriptionTemplate.content.cloneNode(true)
          : null;
        replaceChildren(this.modalDescription, description);
      }

      if (this.modalFooter) {
        this.modalFooter.textContent = footer;
        this.modalFooter.hidden = !footer;
      }
    }

    buildThumbnails() {
      if (!this.thumbnails) return;

      replaceChildren(this.thumbnails);
      var fragment = global.document.createDocumentFragment();
      var total = this.galleryItems.length;

      this.galleryItems.forEach(function (item, index) {
        var wrapper = global.document.createElement('span');
        var button = global.document.createElement('button');
        var image = global.document.createElement('img');

        wrapper.setAttribute('role', 'listitem');
        button.type = 'button';
        button.className = 'cmx-about-events__thumbnail';
        button.dataset.cmxAboutEventsThumbnail = '';
        button.dataset.photoIndex = String(index);
        button.setAttribute('aria-label', 'Mostrar foto ' + String(index + 1) + ' de ' + String(total));
        button.setAttribute('aria-current', index === 0 ? 'true' : 'false');

        image.src = item.thumbnailSrc;
        if (item.thumbnailSrcset) image.srcset = item.thumbnailSrcset;
        image.sizes = '76px';
        image.width = 76;
        image.height = 57;
        image.loading = 'lazy';
        image.alt = '';

        button.appendChild(image);
        wrapper.appendChild(button);
        fragment.appendChild(wrapper);
      });

      this.thumbnails.appendChild(fragment);
    }

    showPhoto(index) {
      if (!this.galleryItems.length || !this.mainImage) return;

      var position = clamp(index, 0, this.galleryItems.length - 1);
      var item = this.galleryItems[position];
      this.activePhotoIndex = position;

      this.mainImage.width = item.width;
      this.mainImage.height = item.height;
      this.mainImage.alt = item.alt;
      this.mainImage.decoding = 'async';
      this.mainImage.sizes = '(min-width: 750px) 62vw, 100vw';
      if (item.srcset) this.mainImage.srcset = item.srcset;
      else this.mainImage.removeAttribute('srcset');
      this.mainImage.src = item.src;

      if (this.photoCounter) {
        this.photoCounter.textContent = String(position + 1) + ' de ' + String(this.galleryItems.length);
      }

      if (this.caption) {
        this.caption.textContent = item.caption;
        this.caption.hidden = !item.caption;
      }

      if (this.photoPreviousButton) this.photoPreviousButton.disabled = position <= 0;
      if (this.photoNextButton) this.photoNextButton.disabled = position >= this.galleryItems.length - 1;

      if (this.thumbnails) {
        toArray(this.thumbnails.querySelectorAll('[data-cmx-about-events-thumbnail]'))
          .forEach(function (thumbnail, thumbnailIndex) {
            var active = thumbnailIndex === position;
            thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
            if (active && typeof thumbnail.scrollIntoView === 'function') {
              thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }
          });
      }

      this.preloadAdjacentPhotos(position);
    }

    preloadAdjacentPhotos(position) {
      this.preloadedImages = [];
      [position - 1, position + 1].forEach(function (adjacentIndex) {
        var adjacent = this.galleryItems[adjacentIndex];
        if (!adjacent || !adjacent.src || typeof global.Image !== 'function') return;

        var preload = new global.Image();
        preload.decoding = 'async';
        preload.src = adjacent.src;
        this.preloadedImages.push(preload);
      }, this);
    }

    closeGallery() {
      if (!this.dialog) return;

      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
        this.handleDialogClose();
      } else if (this.dialog.hasAttribute('open')) {
        this.dialog.removeAttribute('open');
        this.handleDialogClose();
      }
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.closeGallery();
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.closeGallery();
    }

    handleDialogClose() {
      var trigger = this.activeTrigger;

      this.unlockScroll();
      this.galleryItems = [];
      this.preloadedImages = [];
      this.activePhotoIndex = 0;
      this.pointerStart = null;
      this.activeTrigger = null;

      if (this.mainImage) {
        this.mainImage.removeAttribute('src');
        this.mainImage.removeAttribute('srcset');
        this.mainImage.alt = '';
      }
      replaceChildren(this.thumbnails);

      if (trigger && trigger.isConnected && typeof trigger.focus === 'function') {
        trigger.focus();
      }
    }

    handleDialogKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showPhoto(this.activePhotoIndex - 1);
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showPhoto(this.activePhotoIndex + 1);
        return;
      }

      if (event.key !== 'Tab') return;

      var focusable = toArray(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter(function (element) {
          return !element.hidden
            && (element.offsetParent !== null || element.getClientRects().length > 0);
        });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = global.document.activeElement;

      if (event.shiftKey && (active === first || active === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handlePointerDown(event) {
      if (!event.isPrimary || event.button !== 0) return;
      if (event.target.closest('button')) return;

      this.pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY
      };
    }

    handlePointerUp(event) {
      if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;

      var deltaX = event.clientX - this.pointerStart.x;
      var deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;

      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      this.showPhoto(this.activePhotoIndex + (deltaX < 0 ? 1 : -1));
    }

    handlePointerCancel() {
      this.pointerStart = null;
    }

    lockScroll() {
      if (this.scrollLockState || !global.document) return;

      var body = global.document.body;
      var root = global.document.documentElement;
      var scrollY = global.scrollY || global.pageYOffset || 0;
      var scrollbarWidth = Math.max(0, global.innerWidth - root.clientWidth);

      this.scrollLockState = {
        scrollY: scrollY,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyWidth: body.style.width,
        bodyPaddingRight: body.style.paddingRight,
        rootOverflow: root.style.overflow
      };

      root.classList.add('cmx-about-events-modal-open');
      body.classList.add('cmx-about-events-modal-open');
      root.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = '-' + String(scrollY) + 'px';
      body.style.width = '100%';
      if (scrollbarWidth > 0) body.style.paddingRight = String(scrollbarWidth) + 'px';
    }

    unlockScroll() {
      if (!this.scrollLockState || !global.document) return;

      var body = global.document.body;
      var root = global.document.documentElement;
      var state = this.scrollLockState;
      this.scrollLockState = null;

      root.classList.remove('cmx-about-events-modal-open');
      body.classList.remove('cmx-about-events-modal-open');
      root.style.overflow = state.rootOverflow;
      body.style.position = state.bodyPosition;
      body.style.top = state.bodyTop;
      body.style.width = state.bodyWidth;
      body.style.paddingRight = state.bodyPaddingRight;
      global.scrollTo({ top: state.scrollY, left: 0, behavior: 'auto' });
    }
  }

  if (!global.customElements.get(ELEMENT_NAME)) {
    global.customElements.define(ELEMENT_NAME, CmxAboutEventsGalleryElement);
  }

  global.CmxAboutEventsGallery = {
    Element: CmxAboutEventsGalleryElement,
    buildMeta: buildMeta,
    clamp: clamp,
    getClosestCardIndex: getClosestCardIndex,
    hasRealOverflow: hasRealOverflow
  };
})(window);
